import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import {
  ArrowLeftIcon, StarIcon, GlobeIcon, XLogoIcon, TelegramLogoIcon,
  ArrowSquareOutIcon, SpinnerGapIcon, ShareNetworkIcon, SkullIcon,
  LinkSimpleIcon, ArrowsLeftRightIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { fetchCoin, trackEvent } from "../lib/api";
import { fetchRugcheck } from "../lib/api";
import { fetchDeployer } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { DegenScore, FlagBadge, verdictColor, MomentumBadge } from "../components/DegenScore";
import { TradeButton } from "../components/TradeButton";
import { RugForensics } from "../components/RugForensics";
import { StructurePanel } from "../components/StructurePanel";
import { TechScorePanel } from "../components/TechScorePanel";
import { CabalScan } from "../components/CabalScan";
import { TechnicalIndicators } from "../components/TechnicalIndicators";
import { CoinShareCard } from "../components/CoinShareCard";
import { CaughtEarlyCard } from "../components/CaughtEarlyCard";
import { GraduationCard } from "../components/GraduationCard";
import { ApeWatermark } from "../components/ApeWatermark";
import { CreatorRugBadge } from "../components/RugControls";
import { AlphaBadge } from "../components/AlphaBadge";
import { EliteBadge, EliteChecklist } from "../components/EliteBadge";
import { ParabolicTopWarning } from "../components/ParabolicTopWarning";
import { ScoreHistory } from "../components/ScoreHistory";
import { TokenSummary } from "../components/TokenSummary";
import { TokenAutopsyCard } from "../components/TokenAutopsyCard";
import { CompareTokensModal } from "../components/CompareTokensModal";
import { fmtUsd, fmtPrice, fmtPct, fmtAge, changeColor, scoreColor } from "../lib/format";
import { useWatchlist } from "../context/WatchlistContext";
import { useTrash, useCommunityTrash } from "../lib/useTrash";
import { FollowBuilderButton } from "../components/FollowBuilderButton";
import { TrackDevWalletButton, TrackTopHolderButton } from "../components/TrackDevWalletButton";
import { CtoBadge, CtoPanel } from "../components/CtoBadge";
import { CandleBadge } from "../components/CandleBadge";
import { Tier3Badge } from "../components/Tier3Badge";
import { HolderFlow } from "../components/HolderFlow";

const Metric = ({ label, value, color }) => (
  <div className="border border-white/10 bg-card p-4">
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono font-bold text-lg mt-1" style={color ? { color } : {}}>{value}</div>
  </div>
);

const MomentumPanel = ({ momentum }) => {
  if (!momentum) return null;
  const m = momentum;
  return (
    <div className="border border-white/10 bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-lg">Momentum &amp; Reversal</h2>
        <MomentumBadge momentum={m} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Volume spike</div>
          <div className="font-mono font-bold text-lg" style={{ color: m.spike >= 3 ? "#00E599" : m.spike >= 1.5 ? "#F5A623" : "#fff" }}>
            {m.spike}x
          </div>
          <div className="text-[10px] text-muted-foreground">vs its hourly average</div>
        </div>
        <div className="border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last-hour share</div>
          <div className="font-mono font-bold text-lg">{m.concentration}%</div>
          <div className="text-[10px] text-muted-foreground">of 24h volume</div>
        </div>
        <div className="border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">1h buys / sells</div>
          <div className="font-mono font-bold text-lg">
            <span className="text-neon">{m.buysH1}</span> / <span className="text-danger">{m.sellsH1}</span>
          </div>
          <div className="text-[10px] text-muted-foreground">{m.buyRatioH1}% buys</div>
        </div>
        <div className="border border-white/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Price velocity</div>
          <div className="font-mono font-bold text-lg" style={{ color: changeColor(m.changeH1) }}>{fmtPct(m.changeH1)}</div>
          <div className="text-[10px] text-muted-foreground">1h · 5m {fmtPct(m.changeM5)}</div>
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
        A high volume spike with buy pressure often signals a coin waking up; a spike with sell pressure warns of a dump. Not financial advice.
      </p>
    </div>
  );
};

const XActivity = ({ xActivity, socials }) => {
  const twitter = (socials || []).find((s) => s.type === "twitter");
  if (!twitter && !xActivity?.handle) return null;
  const href = twitter?.url || `https://x.com/${xActivity?.handle}`;
  const a = xActivity || {};
  let status = null;
  if (a.configured && a.daysAgo != null) {
    const dormant = a.dormant;
    status = (
      <span className="font-mono text-xs" style={{ color: dormant ? "#FF3366" : a.daysAgo <= 3 ? "#00E599" : "#F5A623" }}>
        {dormant ? "🔴 " : "🟢 "}Last post {a.daysAgo < 1 ? "today" : `${a.daysAgo}d ago`}{dormant ? " · looks dormant" : ""}
      </span>
    );
  } else if (a.rateLimited) {
    status = <span className="font-mono text-xs text-muted-foreground">X rate-limited, try later</span>;
  } else if (a.needsUpgrade) {
    status = <span className="font-mono text-[10px] text-muted-foreground">post-recency needs X API Basic tier (Free tier can't read posts)</span>;
  } else if (a.notFound) {
    status = <span className="font-mono text-xs text-danger">🔴 X account not found</span>;
  } else if (a.configured === false) {
    status = <span className="font-mono text-[10px] text-muted-foreground">post-recency needs X API key</span>;
  }
  return (
    <div className="border border-white/10 bg-card p-5">
      <h2 className="font-heading font-bold text-lg mb-3">X / Twitter</h2>
      <a
        data-testid="x-activity-link"
        href={href}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-between gap-2 px-3 py-2.5 border border-white/10 hover:border-neon/40 hover:text-neon transition-colors group"
      >
        <span className="flex items-center gap-2 text-sm font-mono truncate">
          <XLogoIcon size={16} /> @{xActivity?.handle || twitter?.url?.split("/").pop()}
        </span>
        <ArrowSquareOutIcon size={14} className="text-muted-foreground group-hover:text-neon" />
      </a>
      {status && <div className="mt-2">{status}</div>}
    </div>
  );
};

const socialIcon = (type) => {
  if (type === "twitter") return XLogoIcon;
  if (type === "telegram") return TelegramLogoIcon;
  return GlobeIcon;
};

const DeployerRapSheet = ({ deployer, creator, symbol, mint }) => {
  if (!creator) return null;
  const d = deployer || {};
  const proven = d.isHighQualityDeployer || d.hasPreviousRunners;
  const spammer = d.isSerialSpammer;
  const accent = spammer ? "#FF3366" : proven ? "#00E599" : "#8A8F98";
  const short = `${creator.slice(0, 4)}…${creator.slice(-4)}`;
  return (
    <div className="border bg-card p-5" style={{ borderColor: accent + "40" }} data-testid="deployer-rap-sheet">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-heading font-bold text-lg">Deployer Rap Sheet</h2>
        {deployer && (
          <span className="font-mono text-xs px-2 py-0.5 border" style={{ color: accent, borderColor: accent + "55" }}>
            {spammer ? "☠️ SERIAL SPAMMER" : proven ? "⭐ PROVEN" : "UNPROVEN"}
          </span>
        )}
      </div>
      <a
        href={`https://solscan.io/account/${creator}`}
        target="_blank"
        rel="noreferrer"
        data-testid="deployer-wallet-link"
        className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground hover:text-neon transition-colors mb-4"
      >
        {short} <ArrowSquareOutIcon size={12} />
      </a>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <TrackDevWalletButton creator={creator} symbol={symbol} />
        <TrackTopHolderButton mint={mint} symbol={symbol} />
        {proven && <FollowBuilderButton creator={creator} />}
      </div>
      {deployer && (
      <div className="grid grid-cols-4 gap-2.5 text-center">
        <div className="border border-white/10 bg-black/20 py-2.5">
          <div className="font-mono font-bold text-base">{d.totalTokens}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">Tokens</div>
        </div>
        <div className="border border-white/10 bg-black/20 py-2.5">
          <div className="font-mono font-bold text-base" style={{ color: d.runnerCount ? "#00E599" : "#888" }}>{d.runnerCount}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">$1M+</div>
        </div>
        <div className="border border-white/10 bg-black/20 py-2.5">
          <div className="font-mono font-bold text-base" style={{ color: d.strongRunnerCount ? "#00E599" : "#888" }}>{d.strongRunnerCount}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">$5M+</div>
        </div>
        <div className="border border-white/10 bg-black/20 py-2.5">
          <div className="font-mono font-bold text-base">{Math.round((d.successRate || 0) * 100)}%</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">Hit rate</div>
        </div>
      </div>
      )}
      {d.flags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {d.flags.map((f, i) => (
            <span key={i} className="font-mono text-[10px] px-2 py-1 border border-white/12 text-muted-foreground">{f}</span>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
        This wallet's past-launch track record across AlphaApeing's index — how many of its coins hit $1M / $5M peak market cap. A deployer's history is a signal, not a guarantee. Not financial advice.
      </p>
    </div>
  );
};

const PreGradCoin = ({ coin, onBack, deployer }) => {
  const progress = coin.bondingProgress || 0;
  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="pregrad-view">
      <button data-testid="back-btn" onClick={onBack} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors">
        <ArrowLeftIcon size={16} /> Back
      </button>

      <div className="border border-neon/25 bg-gradient-to-br from-neon/[0.06] to-transparent p-5 sm:p-6">
        <div className="flex items-center gap-4">
          {coin.image ? (
            <img src={coin.image} alt={coin.symbol} className="w-16 h-16 object-cover border border-white/10" onError={(e) => (e.target.style.display = "none")} />
          ) : (
            <div className="w-16 h-16 flex items-center justify-center bg-white/5 border border-white/10 font-mono">{(coin.symbol || "?").slice(0, 3)}</div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading font-black text-3xl tracking-tighter">${(coin.symbol || "?").replace("$", "")}</h1>
              <span className="font-mono text-[10px] px-2 py-1 border border-neon/40 text-neon bg-neon/[0.06]">🍼 PRE-GRADUATION · pump.fun bonding curve</span>
            </div>
            <p className="text-muted-foreground text-sm mt-0.5">{coin.name}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Metric label="Market Cap" value={fmtUsd(coin.marketCap)} />
          <Metric label="Age" value={fmtAge(coin.ageHours)} />
          <Metric label="Status" value={coin.complete ? "Graduating" : "On curve"} />
        </div>

        <div className="mt-5">
          <div className="flex items-center justify-between text-[11px] font-mono text-muted-foreground mb-1.5">
            <span>Bonding curve progress</span>
            <span className="text-neon">{progress}%</span>
          </div>
          <div className="h-2.5 bg-white/5 overflow-hidden">
            <div className="h-full bg-neon transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
            This token is still trading on the pump.fun bonding curve and hasn't graduated to a DEX yet — so full Degen Score analytics (liquidity safety, buy pressure, survival) unlock once it lists. Trade at your own risk.
          </p>
        </div>

        <a
          href={coin.url}
          target="_blank"
          rel="noreferrer"
          data-testid="pregrad-pumpfun-link"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors"
        >
          Open on pump.fun <ArrowSquareOutIcon size={16} weight="bold" />
        </a>
      </div>

      {coin.creator && <DeployerRapSheet deployer={deployer} creator={coin.creator} symbol={coin.symbol} mint={coin.address} />}
    </div>
  );
};

export default function CoinDetail() {
  const { address } = useParams();
  const navigate = useNavigate();
  const { isWatched, toggle } = useWatchlist();
  const { isTrashed, trash, untrash } = useTrash();
  const { isCommunityScam, scamCount } = useCommunityTrash();
  const [showShare, setShowShare] = useState(false);
  const [showEarly, setShowEarly] = useState(false);
  const [showGrad, setShowGrad] = useState(false);
  const [showAutopsy, setShowAutopsy] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: coin, isLoading, isError, refetch, isRefetching, failureCount } = useQuery({
    queryKey: ["coin", address],
    queryFn: () => fetchCoin(address),
    refetchInterval: 30000,
    retry: (count, err) => (err?.response?.status ?? 500) >= 500 && count < 4,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
  const { data: rugData } = useQuery({
    queryKey: ["rugcheck", address],
    queryFn: () => fetchRugcheck(address),
    staleTime: 300000,
  });

  const creatorAddr = coin?.creator || rugData?.creator;
  const { data: deployer } = useQuery({
    queryKey: ["deployer", creatorAddr],
    queryFn: () => fetchDeployer(creatorAddr),
    enabled: !!creatorAddr,
    staleTime: 600000,
  });

  useEffect(() => {
    if (!coin?.address) return;
    const scoredAt = coin.score?.scoredAt;
    const fresh = !!scoredAt && (Date.now() / 1000 - scoredAt < 300);
    trackEvent("report_view", getClientId(), { mint: coin.address, fresh });
  }, [coin?.address]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!coin) return;
    const share = searchParams.get("share");
    if (!share) return;
    if (share === "graduation" && coin.graduate) setShowGrad(true);
    else setShowShare(true);
    searchParams.delete("share");
    setSearchParams(searchParams, { replace: true });
  }, [coin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-muted-foreground gap-3">
        <SpinnerGapIcon size={32} className="animate-spin text-neon" />
        <span className="font-mono text-sm">{failureCount > 0 ? "Reconnecting… retrying" : "Loading token…"}</span>
      </div>
    );
  }
  if (isError || !coin) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-24 text-center flex flex-col items-center gap-4">
        <p className="text-danger font-mono">Couldn't load this token right now.</p>
        <div className="flex items-center gap-3">
          <button
            data-testid="coin-retry-btn"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="flex items-center gap-2 px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors disabled:opacity-50"
          >
            {isRefetching ? <SpinnerGapIcon size={16} className="animate-spin" /> : <ArrowLeftIcon size={16} className="rotate-180" />}
            {isRefetching ? "Retrying…" : "Try again"}
          </button>
          <button data-testid="back-btn" onClick={() => navigate("/")} className="text-neon underline text-sm">Back to Radar</button>
        </div>
      </div>
    );
  }

  if (coin.bondingCurve) {
    return <PreGradCoin coin={coin} onBack={() => navigate(-1)} deployer={deployer} />;
  }

  const watched = isWatched(coin.address);
  const trashed = isTrashed(coin.address);
  const scam = isCommunityScam(coin.address);
  const scamN = scamCount(coin.address);
  const coinWithCreator = { ...coin, creator: coin.creator || rugData?.creator };
  const ch24 = coin.priceChange?.h24 ?? 0;
  const chartData = (coin.history || []).map((h) => ({ label: h.t, price: h.price }));
  const lineColor = changeColor(ch24);
  const txns24 = coin.txns?.h24 || {};

  const _agoSec = coin.score?.scoredAt ? Math.max(0, Math.floor(Date.now() / 1000 - coin.score.scoredAt)) : null;
  const scoreFreshness = _agoSec == null
    ? "Live on-chain data"
    : `Data as of ${_agoSec < 60 ? `${_agoSec}s` : _agoSec < 3600 ? `${Math.floor(_agoSec / 60)}m` : `${Math.floor(_agoSec / 3600)}h`} ago · DexScreener + RugCheck`;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors">
        <ArrowLeftIcon size={16} /> Back
      </button>

      {/* Header */}
      <div className="border border-white/10 bg-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
        <div className="flex items-center gap-4">
          {coin.image ? (
            <img src={coin.image} alt={coin.symbol} className="w-16 h-16 object-cover border border-white/10" onError={(e) => (e.target.style.display = "none")} />
          ) : (
            <div className="w-16 h-16 flex items-center justify-center bg-white/5 border border-white/10 font-mono">{(coin.symbol || "?").slice(0, 3)}</div>
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading font-black text-3xl tracking-tighter">{coin.symbol}</h1>
              <span className="font-mono text-xs px-2 py-1 border" style={{ color: verdictColor(coin.score.verdict), borderColor: verdictColor(coin.score.verdict) + "40" }}>
                {coin.score.verdict}
              </span>
              <CreatorRugBadge creator={coinWithCreator.creator} />
              <AlphaBadge creator={coinWithCreator.creator} />
              <EliteBadge coin={coin} />
              <Tier3Badge address={coin.address} symbol={coin.symbol} />
              {coin.discovery === "organic" && (
                <span data-testid="detail-organic-badge" title="Organic — surfaced via trending / high-volume pools. No paid promotion; real demand."
                  className="font-mono text-xs px-2 py-1 bg-neon/10 border border-neon/40 text-neon">🌱 ORGANIC</span>
              )}
              {coin.discovery === "boosted" && (
                <span data-testid="detail-boosted-badge" title="Paid Boosted — appeared via a paid DexScreener promotion. Someone paid to surface it; verify the demand is real."
                  className="font-mono text-xs px-2 py-1" style={{ background: "rgba(240,192,0,0.12)", border: "1px solid rgba(240,192,0,0.45)", color: "#F0C000" }}>💸 BOOSTED</span>
              )}
              {trashed && (
                <span data-testid="detail-trash-badge" title="You marked this as trash/scam"
                  className="font-mono text-xs px-2 py-1 bg-danger/20 border border-danger/50 text-danger">🗑️ TRASH</span>
              )}
              {scam && (
                <span data-testid="detail-scam-badge" title={`Flagged as a scam by ${scamN} users`}
                  className="font-mono text-xs px-2 py-1 bg-danger/25 border border-danger/60 text-danger font-bold">☠️ SCAM {scamN}</span>
              )}
              <CtoBadge cto={coin.cto} testid="detail-cto-badge" />
              <CandleBadge candle={coin.candle} size="md" />
            </div>
            <p className="text-muted-foreground text-sm">{coin.name}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="font-mono text-2xl font-bold">{fmtPrice(coin.priceUsd)}</span>
              <span className="font-mono text-sm" style={{ color: changeColor(ch24) }}>{fmtPct(ch24)} 24h</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center">
            <DegenScore score={coin.score.total} size={72} showLabel />
          </div>
          <div className="flex flex-col gap-2">
            <TradeButton coin={coinWithCreator} />
            <div className="flex gap-2">
              <button
                data-testid="detail-watch-btn"
                onClick={() => toggle(coin.address)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold border transition-colors ${
                  watched ? "bg-warn/10 border-warn/40 text-warn" : "border-white/20 hover:border-white/50"
                }`}
              >
                <StarIcon size={16} weight={watched ? "fill" : "regular"} />
                {watched ? "Watching" : "Watchlist"}
              </button>
              <a
                data-testid="dexscreener-link"
                href={coin.url}
                target="_blank"
                rel="noreferrer"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold border border-white/20 hover:border-neon/50 hover:text-neon transition-colors"
              >
                <ArrowSquareOutIcon size={16} weight="bold" /> Chart
              </a>
            </div>
            <button
              data-testid="share-flex-btn"
              onClick={() => setShowShare(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold border border-neon/40 bg-neon/[0.06] text-neon hover:bg-neon/15 transition-colors"
            >
              <ShareNetworkIcon size={16} weight="bold" /> Share flex card
            </button>
            <div className="grid grid-cols-3 gap-2">
              <button
                data-testid="copy-report-link-btn"
                onClick={() => {
                  navigator.clipboard.writeText(`https://alphaapeing.com/token/${coin.address}`)
                    .then(() => toast.success("Report link copied"))
                    .catch(() => toast.error("Copy failed"));
                }}
                className="flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold border border-white/15 hover:border-neon/40 hover:text-neon transition-colors"
              >
                <LinkSimpleIcon size={14} weight="bold" /> Copy link
              </button>
              <button
                data-testid="compare-token-btn"
                onClick={() => setShowCompare(true)}
                className="flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold border border-white/15 hover:border-neon/40 hover:text-neon transition-colors"
              >
                <ArrowsLeftRightIcon size={14} weight="bold" /> Compare
              </button>
              <button
                data-testid="autopsy-btn"
                onClick={() => setShowAutopsy(true)}
                className="flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold border border-warn/40 bg-warn/[0.06] text-warn hover:bg-warn/15 transition-colors"
              >
                🔬 Autopsy
              </button>
            </div>
            {coin.velocity?.direction === "up" && (
              <button
                data-testid="caught-early-btn"
                onClick={() => setShowEarly(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold border border-[#F0C000]/50 bg-[#F0C000]/[0.08] text-[#F0C000] hover:bg-[#F0C000]/15 transition-colors"
              >
                🚀 Flex your {coin.velocity.multiplier}× (caught early)
              </button>
            )}
            {coin.graduate && (
              <button
                data-testid="graduation-btn"
                onClick={() => setShowGrad(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold border border-[#22D3EE]/50 bg-[#22D3EE]/[0.08] text-[#22D3EE] hover:bg-[#22D3EE]/15 transition-colors"
              >
                🎓 Caught it at graduation
              </button>
            )}
            <button
              data-testid="detail-trash-btn"
              onClick={() => (trashed ? untrash(coin.address) : trash(coin.address, coin.symbol))}
              className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${
                trashed ? "bg-danger/15 border-danger/50 text-danger" : "border-white/15 text-muted-foreground hover:border-danger/50 hover:text-danger"
              }`}
            >
              <SkullIcon size={16} weight="bold" /> {trashed ? "Trashed — tap to restore" : "Mark as trash / scam"}
            </button>
          </div>
        </div>
      </div>

      <CtoPanel coin={coin} onChange={refetch} />

      <HolderFlow address={coin.address} />

      <CoinShareCard coin={coin} open={showShare} onClose={() => setShowShare(false)} />
      <CaughtEarlyCard coin={coin} open={showEarly} onClose={() => setShowEarly(false)} />
      <GraduationCard coin={coin} open={showGrad} onClose={() => setShowGrad(false)} />
      <TokenAutopsyCard coin={coin} open={showAutopsy} onClose={() => setShowAutopsy(false)} />
      <CompareTokensModal coin={coin} open={showCompare} onClose={() => setShowCompare(false)} />

      {scam && (
        <div data-testid="scam-warning" className="border border-danger/50 bg-danger/[0.09] p-4 flex items-start gap-3">
          <span className="text-2xl leading-none">☠️</span>
          <div>
            <div className="font-heading font-bold text-danger">Community-flagged scam</div>
            <p className="text-sm text-muted-foreground mt-0.5">
              <span className="text-danger font-semibold">{scamN} AlphaApeing users</span> have marked this token as trash/scam.
              Treat with extreme caution and do your own research before aping.
            </p>
          </div>
        </div>
      )}

      {coin.vamp?.status === "vamp" && (
        <div data-testid="vamp-warning" className="border border-danger/40 bg-danger/[0.07] p-4 flex items-start gap-3">
          <span className="text-2xl leading-none">🧛</span>
          <div>
            <div className="font-heading font-bold text-danger">Possible VAMP — copycat ticker</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              An <span className="text-white">older ${coin.symbol}</span> token already exists
              {coin.vamp.original ? <> (original ~<span className="text-white">{fmtUsd(coin.vamp.original.mcap)}</span> mcap, {fmtUsd(coin.vamp.original.liq)} liq)</> : ""}.
              This one may be leeching its attention — {coin.vamp.dupes} same-ticker token{coin.vamp.dupes === 1 ? "" : "s"} found. Make sure you're aping the right one.
            </p>
            {coin.vamp.original && (
              <button onClick={() => navigate(`/coin/${coin.vamp.original.address}`)} className="text-xs text-neon hover:underline mt-1.5">View the original ${coin.symbol} →</button>
            )}
          </div>
        </div>
      )}
      {coin.vamp?.status === "og" && coin.vamp.dupes > 0 && (
        <div data-testid="og-panel" className="border border-neon/30 bg-neon/[0.04] p-3 flex items-center gap-3 text-sm">
          <span className="text-lg leading-none">✅</span>
          <span className="text-muted-foreground">This is the <span className="text-neon font-semibold">original ${coin.symbol}</span> ticker — {coin.vamp.dupes} copycat{coin.vamp.dupes === 1 ? "" : "s"} exist. Watch for vamps riding this name.</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Chart + metrics */}
        <div className="lg:col-span-2 space-y-6">
          <TokenSummary coin={coin} rug={rugData} />
          <div className="border border-white/10 bg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-lg">Price Trajectory (24h)</h2>
              <span className="text-[11px] text-muted-foreground font-mono">approx. from timeframe deltas</span>
            </div>
            <div className="relative overflow-hidden">
              <ApeWatermark opacity={0.045} size="180px" />
              <div className="relative z-10">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="label" stroke="#666" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false} width={70} domain={["auto", "auto"]} tickFormatter={(v) => fmtPrice(v).replace("$", "")} />
                    <Tooltip
                      contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontFamily: "JetBrains Mono" }}
                      labelStyle={{ color: "#888" }}
                      formatter={(v) => [fmtPrice(v), "Price"]}
                    />
                    <Line type="monotone" dataKey="price" stroke={lineColor} strokeWidth={2} dot={{ r: 3, fill: lineColor }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <TechnicalIndicators address={coin.address} />

          {coin.traction && (() => {
            const t = coin.traction;
            const tc = t.score >= 55 ? "#00E599" : t.score >= 35 ? "#F0C000" : "#888";
            const Stat = ({ label, value }) => (
              <div className="border border-white/10 bg-black/20 py-2.5">
                <div className="font-mono font-bold text-base">{value}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
              </div>
            );
            return (
              <div className="border border-white/10 bg-card p-5" data-testid="traction-gauge">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-heading font-bold text-lg">Community & Traction</h2>
                  <span className="font-mono text-sm px-2 py-0.5 border" style={{ color: tc, borderColor: tc + "55" }}>{t.label} · {t.score}/100</span>
                </div>
                <div className="h-2 bg-white/5 mb-4"><div className="h-full transition-all duration-500" style={{ width: `${t.score}%`, background: tc }} /></div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-center">
                  <Stat label="Buy pressure" value={`${t.buyRatio24}%`} />
                  <Stat label="Traders 24h" value={(t.traders24 || 0).toLocaleString()} />
                  <Stat label="Activity trend" value={`${t.txGrowth}×`} />
                  <Stat label="Age" value={fmtAge(t.ageHours)} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                  On-chain traction proxy (buy pressure, unique-trader trend, sustained volume, age) — a read on whether a real community is forming after bundlers exit. Not social-follower data.
                </p>
              </div>
            );
          })()}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Metric label="Market Cap" value={fmtUsd(coin.marketCap)} />
            <Metric label="Liquidity" value={fmtUsd(coin.liquidity)} />
            <Metric label="24h Volume" value={fmtUsd(coin.volume?.h24)} />
            <Metric label="Age" value={fmtAge(coin.ageHours)} />
            <Metric label="1h" value={fmtPct(coin.priceChange?.h1)} color={changeColor(coin.priceChange?.h1 ?? 0)} />
            <Metric label="6h" value={fmtPct(coin.priceChange?.h6)} color={changeColor(coin.priceChange?.h6 ?? 0)} />
            <Metric label="Buys 24h" value={txns24.buys ?? "—"} color="#00E599" />
            <Metric label="Sells 24h" value={txns24.sells ?? "—"} color="#FF3366" />
          </div>
        </div>

        {/* Score breakdown */}
        <div className="space-y-6">
          <div className="border border-white/10 bg-card p-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-heading font-bold text-lg">Degen Score Breakdown</h2>
              <Link to="/methodology" data-testid="score-methodology-link" className="text-[11px] font-mono text-neon hover:underline shrink-0">Full methodology →</Link>
            </div>
            <p className="text-[11px] text-muted-foreground font-mono mb-4" data-testid="score-freshness">{scoreFreshness}</p>
            <div className="space-y-4">
              {coin.score.breakdown.map((b) => {
                const unknown = b.value == null;
                const pct = unknown ? 0 : (b.value / b.max) * 100;
                return (
                  <div key={b.key}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-muted-foreground">{b.key}</span>
                      <span className="font-mono" style={unknown ? { color: "#8A8F98" } : {}}>{unknown ? "Unknown" : `${b.value}/${b.max}`}</span>
                    </div>
                    <div className="h-1.5 bg-white/5 overflow-hidden">
                      <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: unknown ? "#8A8F98" : scoreColor(pct) }} />
                    </div>
                    {b.note && <p className="text-[10px] text-muted-foreground/70 mt-1 font-mono leading-snug">{b.note}</p>}
                  </div>
                );
              })}
            </div>
            <div className="mt-5 pt-4 border-t border-white/10 flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="font-mono font-bold text-2xl" style={{ color: scoreColor(coin.score.total) }}>
                {coin.score.total}<span className="text-sm text-muted-foreground">/{coin.score.maxPossible || 100}</span>
              </span>
            </div>
            {coin.score.safetyKnown === false && (
              <p data-testid="score-safety-unconfirmed" className="text-[11px] text-warn/90 mt-2.5 leading-relaxed">
                ⚠️ On-chain safety unconfirmed — up to 20 safety points are unverified, so this score is capped at 80. This is a data gap, not a safety verdict.
              </p>
            )}
          </div>

          <div className="border border-white/10 bg-card p-5">
            <h2 className="font-heading font-bold text-lg mb-4">Signals</h2>
            <div className="flex flex-wrap gap-2">
              {coin.score.flags.map((f, i) => <FlagBadge key={i} flag={f} />)}
            </div>
          </div>

          <StructurePanel address={coin.address} />

          <ScoreHistory mint={coin.address} />

          <DeployerRapSheet deployer={deployer} creator={coinWithCreator.creator} symbol={coin.symbol} mint={coin.address} />

          <MomentumPanel momentum={coin.momentum} />

          <ParabolicTopWarning address={coin.address} />

          <EliteChecklist coin={coin} />

          <RugForensics address={coin.address} />

          {coin.chainId === "solana" && <TechScorePanel address={coin.address} />}

          {coin.chainId === "solana" && <CabalScan address={coin.address} symbol={coin.symbol} />}

          <XActivity xActivity={coin.xActivity} socials={coin.socials} />

          {(coin.websites?.length > 0 || coin.socials?.some((s) => s.type !== "twitter")) && (
            <div className="border border-white/10 bg-card p-5">
              <h2 className="font-heading font-bold text-lg mb-4">Links</h2>
              <div className="flex flex-wrap gap-2">
                {coin.websites?.map((w, i) => (
                  <a key={`w${i}`} href={w.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 border border-white/10 text-sm hover:border-neon/40 hover:text-neon transition-colors">
                    <GlobeIcon size={16} /> Website
                  </a>
                ))}
                {coin.socials?.filter((s) => s.type !== "twitter").map((s, i) => {
                  const Icon = socialIcon(s.type);
                  return (
                    <a key={`s${i}`} href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 border border-white/10 text-sm capitalize hover:border-neon/40 hover:text-neon transition-colors">
                      <Icon size={16} /> {s.type}
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border border-white/10 bg-card p-4">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Contract</div>
            <div className="font-mono text-xs break-all text-muted-foreground">{coin.address}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
